import type { ParsedUrlQuery } from 'node:querystring';
import { Box, Stack } from '@mui/material';
import { messenger } from '@side/next-zendesk-sdk';
import {
  AppLayoutWithNav,
  SidebarContent as PantrySidebarContent,
  Show,
  type SideNavMenu,
  useIsMobile,
} from '@side/pantry';
import { isNil } from 'lodash';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { type ReactNode, useRef, useState } from 'react';
import { useGlobalInteractionsEnabled } from '@/common/atoms/global';
import { useClientGroup } from '@/common/hooks/clientGroups';
import { useBrokerIsExternal } from '@/common/hooks/useBrokerIsExternal';
import { useHasAccessToCreateTeamMember } from '@/common/hooks/useHasAccessToCreateTeamMember';
import { useHasAccessToFeature } from '@/common/hooks/useHasAccessToFeature';
import { AppFeature } from '@/common/hooks/useHasAccessToFeature/types';
import { useInteraction } from '@/common/hooks/useInteraction';
import { useInteractionParams } from '@/common/hooks/useInteractionParams';
import { useMyInformation } from '@/common/hooks/useMyInformation/useMyInformation';
import { TemplateTitle } from '@/components/AppHeader/components/TemplateTitle';
import { SideBar, SideBarSkeleton } from '@/components/SideBar';
import { SideBarContent } from '@/components/SideBar/SideBarContent';
import { useInteractionStatus } from '@/components/SideBar/useInteractionStatus';
import { WithValidInteraction } from '@/components/WithValidInteraction';
import { GQLInteractionType } from '@/generated/graph-api';
import { SidebarContent as BuyerSidebarContent } from '@/views/Buyers/components/Sidebar';
import { getClientLabels } from '@/views/Buyers/utils';
import { PlatformFeeAlert } from '@/views/PlatformFee';
import {
  SidebarContent as ReferralSidebarContent,
  useReferralSummaryText,
} from '@/views/Referrals/components/Sidebar';
import { SidebarContent as TemplatesSidebarContent } from '@/views/Templates/components/Sidebar';
import { useGetTemplateDetails } from '@/views/Templates/hooks/useGetTemplateDetails';
import { useAppFlags } from '$launch-darkly/app-flags';
import { Download1099Alert } from '$tax-documents';

const AIChat = dynamic(
  () => import('../AIChat/AIChat').then((m) => ({ default: m.AIChat })),
  { ssr: false },
);

interface Props {
  hideInteractionSidebar?: boolean;
  hideNavigation?: boolean | ((query: ParsedUrlQuery) => boolean);
  showInteractionAddress?: boolean;
  defaultExpandedSubmenu?: SideNavMenu;
  children: ReactNode;
}

interface AppShellWithNavProps {
  hideNavigation?: boolean;
  defaultExpandedSubmenu?: SideNavMenu;
  showInteractionAddress?: boolean;
  hideInteractionSidebar?: boolean;
  children: ReactNode;
}

const AppShellWithNav = ({
  hideNavigation = false,
  showInteractionAddress = false,
  hideInteractionSidebar = false,
  defaultExpandedSubmenu,
  children,
}: AppShellWithNavProps) => {
  const { txmSideXSideAiChat } = useAppFlags();
  const { isExternal: brokerIsExternal } = useBrokerIsExternal();
  const isMobile = useIsMobile('md');
  const router = useRouter();
  const { interactionId, interactionType } = useInteractionParams();
  const user = useMyInformation();
  const isGlobalInteractionsOn = useGlobalInteractionsEnabled();
  const hasAccessToLeases = useHasAccessToFeature(AppFeature.LEASES);
  const hasAccessToCreateTeamMember = useHasAccessToCreateTeamMember();
  const mobileHeaderRef = useRef({ close: () => null });

  const [isAiChatOpen, setIsAiChatOpen] = useState(false);

  const { clientGroup } = useClientGroup(
    interactionType === GQLInteractionType.clientGroup
      ? interactionId
      : undefined,
  );

  const skipInteractionQueries = !showInteractionAddress;
  const { data: interaction } = useInteraction(
    interactionId ?? undefined,
    interactionType ?? undefined,
    skipInteractionQueries,
  );

  const {
    isArchived,
    statuses,
    loading: statusLoading,
    status,
    onChangeStatus,
    isTransactionStatusLocked,
  } = useInteractionStatus(skipInteractionQueries);

  const leaseRepresentation = interaction?.representation ?? null;
  const { summary: referralSummaryText } = useReferralSummaryText();
  const { template } = useGetTemplateDetails();

  const { street1, street2, city, state } = interaction?.property ?? {};
  const fullAddress = [street1, street2, city, state]
    .filter((address) => !isNil(address))
    .join(', ');

  const handleOnLinksClick = () => {
    mobileHeaderRef.current.close();
  };

  let mobileHeaderTitle: ReactNode;
  let mobileHeaderContent: ReactNode;

  if (showInteractionAddress && interaction) {
    mobileHeaderTitle = fullAddress;
    mobileHeaderContent = (
      <PantrySidebarContent
        linkComponent={NextLink}
        withNavWrapper
        address={fullAddress}
        interactionType={interactionType}
        leaseRepresentation={leaseRepresentation ?? undefined}
        showArchivedMessage={isArchived}
        basePath={router.basePath}
        TransactionStatusDropdownProps={{
          statuses,
          value: status,
          onChange: (e) => {
            void onChangeStatus(e.target.value);
          },
          loading: statusLoading,
          isTransactionStatusLocked,
        }}
      >
        <SideBarContent onCoversheetLinksClick={handleOnLinksClick} />
      </PantrySidebarContent>
    );
  } else if (
    interactionType === GQLInteractionType.clientGroup &&
    interactionId
  ) {
    const clientNames = (clientGroup?.clients ?? [])
      .map((client) => getClientLabels(client))
      .map(({ label }) => label)
      .filter((label) => !!label)
      .join(', ');

    mobileHeaderTitle = clientNames;
    mobileHeaderContent = <BuyerSidebarContent />;
  } else if (referralSummaryText) {
    mobileHeaderTitle = referralSummaryText;
    mobileHeaderContent = <ReferralSidebarContent />;
  } else if (template) {
    mobileHeaderTitle = <TemplateTitle />;
    mobileHeaderContent = <TemplatesSidebarContent />;
  }

  const content = (
    <Stack
      direction="row"
      sx={{
        // When `hideNavigation` is true we return this Stack directly, with no
        // `AppLayoutWithNav` ancestor to establish a viewport-height flex
        // context — so `height: '100%'` would resolve to auto and collapse to
        // content height. Size against the viewport directly in that case.
        height: hideNavigation ? '100dvh' : '100%',
        maxHeight: '100dvh',
        overflow: 'hidden',
      }}
    >
      <Show when={!hideInteractionSidebar && !isMobile}>
        {/* On a server error the main content shows the whole-page error;
            rendering it in the sidebar too would duplicate it. */}
        <WithValidInteraction
          fallback={<SideBarSkeleton />}
          errorFallback={null}
        >
          <SideBar />
        </WithValidInteraction>
      </Show>
      <main
        style={{
          display: 'flex',
          flex: 1,
          flexDirection: 'column',
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <Show when={isAiChatOpen && txmSideXSideAiChat}>
          <AIChat onClose={() => setIsAiChatOpen(false)} />
        </Show>
        <Box
          sx={{
            position: 'sticky',
            top: '0',
            width: '100%',
            zIndex: '1000',
          }}
        >
          <PlatformFeeAlert />
          <Download1099Alert />
        </Box>
        {children}
      </main>
    </Stack>
  );

  if (hideNavigation) {
    return content;
  }

  return (
    <AppLayoutWithNav
      navProps={{
        variant: isGlobalInteractionsOn ? 'alert' : 'default',
        user,
        homePath: '/',
        basePath: router.basePath,
        showLeasesLink: hasAccessToLeases,
        showCreateTeamMemberLink: hasAccessToCreateTeamMember,
        hideMarketplaceLinks: brokerIsExternal,
        hideBuilderProgramLink: brokerIsExternal,
        hideWorkvivoLink: brokerIsExternal,
        hidePartnerMapLink: brokerIsExternal,
        hideAgentPoliciesLink: brokerIsExternal,
        LinkComponent: NextLink,
        defaultExpandedSubmenu,
        onChat: messenger.open,
        mobileHeaderTitle,
        mobileHeaderContent,
        mobileHeaderRef,
        onSideAIClick: txmSideXSideAiChat
          ? () => setIsAiChatOpen(true)
          : undefined,
      }}
    >
      {content}
    </AppLayoutWithNav>
  );
};

export const AppShell = ({
  hideInteractionSidebar,
  hideNavigation,
  ...props
}: Props) => {
  const { query } = useRouter();

  return (
    <AppShellWithNav
      hideNavigation={
        typeof hideNavigation === 'function'
          ? hideNavigation(query)
          : hideNavigation
      }
      {...props}
      hideInteractionSidebar={hideInteractionSidebar}
    />
  );
};
